/**
 * VISTOERA_SAAS_I18N_FOUNDATION_AND_I18N_FIRST_DEVELOPMENT_STANDARD_V1
 *
 * Canonical type surface for the i18n foundation. This file intentionally
 * has ZERO runtime logic — it only names the three independent language
 * concepts (spec §1) and the V1 supported UI locales (spec §2), so every
 * other module (config, provider, switcher, tests) imports the same
 * literal union instead of re-deriving it from a string.
 *
 * ---------------------------------------------------------------------
 * THREE INDEPENDENT LANGUAGE CONCEPTS (spec §1) — do not conflate them:
 *
 * A. UiLocale             — controls SaaS chrome (nav/button/label/
 *                            tooltip/modal/toast/table/status/form
 *                            validation/Canvas UI chrome/Platform Admin
 *                            UI). This is the ONLY concept this V1 round
 *                            implements end-to-end.
 * B. ContentLanguage       — controls the language of a user's
 *                            AI-generated content (project copy, video
 *                            scripts, prompts...). Completely separate
 *                            from UiLocale. NOT implemented this round —
 *                            existing business behavior is preserved
 *                            byte-for-byte (spec §25/§52). This type
 *                            exists purely so no future code accidentally
 *                            reuses `UiLocale` for this concept.
 * C. AiInteractionLanguage — controls the language an AI Assistant/Agent/
 *                            Skill uses to talk to the user. V1 may default
 *                            it to "follow UiLocale" at the call site, but
 *                            it must never be hardwired into UiLocale
 *                            itself — spec §1/§25 explicitly forbid writing
 *                            `uiLocale` straight into a Skill's
 *                            `output_language`.
 * ---------------------------------------------------------------------
 */

/** V1 supported UI locales (spec §2). zh-CN is DEFAULT + FALLBACK. */
export const SUPPORTED_UI_LOCALES = ['zh-CN', 'en-US'] as const

export type UiLocale = (typeof SUPPORTED_UI_LOCALES)[number]

export const DEFAULT_UI_LOCALE: UiLocale = 'zh-CN'
export const FALLBACK_UI_LOCALE: UiLocale = 'zh-CN'

/** Content Language — deliberately a distinct type from `UiLocale` (spec
 * §1.B/§25). Not persisted/switchable anywhere yet in V1; declared here so
 * a future round has a canonical name to extend instead of inventing a
 * second one. Never derive this from `UiLocale` in business code. */
export type ContentLanguage = string

/** AI Interaction Language — distinct type from `UiLocale` (spec §1.C).
 * V1 does not persist this independently; call sites that need "what
 * language should the Assistant speak" may default to the current
 * `UiLocale` value explicitly at the call site, but must import this type
 * (not `UiLocale`) so the intent is legible and the two can diverge later
 * without a rename. */
export type AiInteractionLanguage = string

/** Canonical i18n namespaces (spec §5). Keep this list in sync with
 * `frontend/src/i18n/locales/<locale>/*.json` — `i18n:check` (see
 * `frontend/scripts/i18nCheck.mjs`) verifies every namespace here has a
 * matching resource file for every supported locale. */
export const I18N_NAMESPACES = [
  'common',
  'navigation',
  'actions',
  'status',
  'contentProjects',
  'imageAssets',
  'canvas',
  'validation',
  'website',
  'platformAdmin',
  'autoProcess',
  'accountEntry',
  'membership',
  'localPublisher',
  'video',
  'overview',
  'aiChat',
  'virtualIp',
  'imageProcessing',
  'competitiveAnalysis',
  'publishing',
  'marketingAnalytics',
  'textAssets',
  'team',
] as const

export type I18nNamespace = (typeof I18N_NAMESPACES)[number]

/** Where the active `UiLocale` came from — surfaced in dev tooling / tests
 * only, never shown to the end user (spec §15/§16/§51). */
export type UiLocaleSource = 'user-explicit' | 'persisted' | 'browser-detected' | 'default-fallback'
